import React, {useContext, useEffect, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../utils/webInfo";
import callToast, {
    handleChangeData,
    handleDelete,
    mergeWithSelected,
    postData,
    validateFormFields
} from "../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../Components/PageHeading";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../Components/Label";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {Dropdown} from "primereact/dropdown";


interface InitialState {

    user_id: { id?: number, name: string, email: string } | null,
    active_status: { id: number, name: string } | null,
    vehicle_ownership_id: { id: number, registration_number: string, model: string } | null,
}

const AssignDriver = () => {
    const initialState: InitialState = {
        user_id: null,
        vehicle_ownership_id: null,
        active_status: {id: 1, name: 'Active'}
    };
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<InitialState>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const deleteId = useRef<number>(0)

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    const [userFilter, setUserFilter] = useState<string>('');
    const [userOptions, setUserOptions] = useState<{ id: number, name: string, email:string }[]>([]);

    const [vehicleFilter, setVehicleFilter] = useState<string>('');
    const [vehicleOptions, setVehicleOptions] = useState<{ id: number, model: string }[]>([]);

    const statuses = [
        {id: 1, name: 'Active'},
        {id: 0, name: 'Inactive'},
    ];

    // pagination
    const {page, limitData, handlePageChange} = usePagination();

    // endpoint
    const mainTitle = "Assign Driver"
    const tableDataEndpointUrl = `/vehicle-driver-assignments?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-driver-assignments/${updatedId}` : "/vehicle-driver-assignments";

    const accept = () => {
        const deleteParams = {
            id: deleteId.current,
            startEndpoint: "/vehicle-driver-assignments/",
            // finalEndpoint: finalEndpoint,
            name: "Driver Assignment",
            setUpdate: setRefresh,
            token: accessToken,
            toast: toast,
        }

        handleDelete(deleteParams).then()
    }


    const {data: userRawData} = useFetch(
        `/employees?page=${page}&per_page=${limitData}${userFilter ? `&name=${userFilter}` : ''}`,
        [userFilter]
    );

    useEffect(() => {
        if (userRawData) {
            const modifiedData = userRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
                email: item?.email,
            }));
            setUserOptions(mergeWithSelected(modifiedData, formData?.user_id));
        }
    }, [userRawData, formData?.user_id]);


    const {data: vehicleRawData} = useFetch(
        `/vehicle-ownership?page=1&per_page=10${vehicleFilter ? `&registration_number=${vehicleFilter}` : ''}`,
        [vehicleFilter]
    );

    useEffect(() => {
        if (vehicleRawData) {
            const modifiedData = vehicleRawData?.map((item: any) => ({
                id: item?.id,
                registration_number: item?.registration_number,
                model: item?.vehicle?.model,
            }));
            setVehicleOptions(mergeWithSelected(modifiedData, formData?.vehicle_ownership_id));
        }
    }, [vehicleRawData, formData?.vehicle_ownership_id]);


    // fetch data
    const {
        data: tableDataList,
        totalData, loading
    } = useFetch(tableDataEndpointUrl, [refresh])


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const validationRules = [
            {field: 'user_id', label: 'Driver'},
            {field: 'vehicle_ownership_id', label: 'Vehicle'},
            {field: 'active_status', label: 'Status'}
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            // name: formData.name.replace(/\s+/g, ' ').trim()
            user_id: formData.user_id?.id,
            vehicle_ownership_id: formData.vehicle_ownership_id?.id,
            active_status: formData.active_status?.id
        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));

            if (response.ok) {
                callToast(toast, true, `${mainTitle} ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState)
                setModal(false)
                setUpdatedId(null)
                setRefresh(!refresh);
                setUserFilter('')
                setVehicleFilter('')
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }

        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }

    }

    // row edit template
    const actionTemplate = (rowData: any) => {
        console.log("edit row data", rowData);
        return (
            <div className={'flex align-items-center justify-content-end gap-2'}>
                <Button
                    icon={'pi pi-pencil'}
                    className={'border-rounded w-2rem h-2rem'}
                    onClick={() => {
                        const updatedStatus = rowData.active_status === 1 ? {id: 1, name: 'Active'} : {
                            id: 0,
                            name: 'Inactive'
                        };
                        setModal(true)
                        setUpdatedId(rowData?.id)
                        setUserFilter('')
                        setVehicleFilter('')
                        setFormData({
                            user_id: rowData?.employee,
                            vehicle_ownership_id: rowData?.vehicle_ownership ? {
                                id: rowData?.vehicle_ownership?.id,
                                registration_number: rowData?.vehicle_ownership?.registration_number,
                                model: rowData?.vehicle_ownership?.vehicle?.model,
                            } : null,
                            active_status: updatedStatus,
                        })
                    }}
                    outlined/>

                <Button
                    icon={'pi pi-trash'}
                    // tooltip={'Delete reservation'}
                    className={'border-rounded w-2rem h-2rem'}
                    outlined
                    onClick={(event: any) => {
                        // setDeletedFloorId(rowData?.id);
                        deleteId.current = rowData?.id;
                        confirmPopup({
                            target: event.currentTarget,
                            message: `Do you want to delete this reservation?`,
                            // message: `Do you want to delete this ${mainTitle.toLowerCase()}`,
                            icon: 'pi pi-info-circle',
                            acceptClassName: 'p-button-danger',
                            accept,
                            onHide() {
                                deleteId.current = 0;
                            }
                        })
                    }}
                />
            </div>
        )
    }

    const vehicleInformationBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold text-sm text-900">{rowData?.vehicle_ownership?.vehicle?.model}</span>
                <span className="text-xs text-700">{rowData?.vehicle_ownership?.registration_number}</span>
            </div>
        );
    };

    const ownerInformationBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold text-sm text-900">{rowData?.vehicle_ownership?.user?.name}</span>
                <span className="text-xs text-700">{rowData?.vehicle_ownership?.user?.email}</span>
            </div>
        )
    }

    const driverInformationBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold text-sm text-900">{rowData?.employee?.name}</span>
                <span className="text-xs text-700">{rowData?.employee?.email}</span>
            </div>
        )
    }

    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const ownerTypeBodyTemplate = (rowData: any) => {
        return <span
            className={'w-full'}>{rowData.is_company_owned === 1 ? message("Company Owned", "secondary") : message("Personal Owned", "new")}</span>;
    }

    const formDriverDropdownItemTemplate = (option: any) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.name}</span>
                <span className="text-xs text-700">{option?.email}</span>
            </div>
        );
    }

    const formVehicleDropdownItemTemplate = (option: any) => {

        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.model}</span>
                <span className="text-xs text-700">{option?.registration_number}</span>
            </div>
        )
    }

    const statusDropdownBodyTemplate = (rowData: any) => {
        return <span
            className={'w-full'}>{rowData.active_status === 1 ? message("Active", "approved") : message("Inactive", "cancelled")}</span>;
    }

    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <ConfirmPopup/>
            <PageHeading title={mainTitle}
                         isCreate={true}
                         createFunction={() => setModal(true)}
                         back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column
                    field="vehicle_ownership"
                    header="Vehicle Information"
                    body={vehicleInformationBodyTemplate}
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    field="vehicle_ownership"
                    header="Owner Information"
                    body={ownerInformationBodyTemplate}
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                />

                <Column
                    field={"is_company_owned"}
                    header="Owner Type"
                    style={{width: '20%'}}
                    body={ownerTypeBodyTemplate}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field={"employee"}
                    header="Driver Information"
                    style={{width: '20%'}}
                    body={driverInformationBodyTemplate}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field="status"
                    header="Status"
                    style={{width: '20%'}}
                    body={statusDropdownBodyTemplate}
                    headerClassName={"white-space-nowrap"}
                />

                <Column header={"Action"} rowEditor={true}
                        bodyStyle={{textAlign: 'center'}}
                        body={actionTemplate}
                        headerClassName={"flex justify-content-end"}
                />
            </DataTable>

            <Dialog visible={modal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setUpdatedId(null)
                        setModal(false)
                        setFormData(initialState)
                        setUserFilter('')
                        setVehicleFilter('')
                    }}
            >
                <div>
                    <form className={''}
                          onSubmit={handleSubmit}
                    >
                        <div className="grid ">
                            <div className="col-12  md:col-6">
                                <Label label={"Vehicle"} required={!updatedId}/>
                                <Dropdown
                                    options={vehicleOptions ? vehicleOptions : []}
                                    optionLabel="registration_number"
                                    id="vehicle_ownership_id"
                                    name={`vehicle_ownership_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_ownership_id}
                                    onChange={(e) => handleChangeData('vehicle_ownership_id', e.value, formData, setFormData)}
                                    placeholder="Select vehicle"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setVehicleFilter(e.filter)
                                        }, 500);

                                        if (vehicleRawData) {
                                            const modifiedData = vehicleRawData.map((item: any) => ({
                                                id: item?.id,
                                                registration_number: item?.registration_number,
                                                model: item?.vehicle?.model,
                                            }));
                                            setVehicleOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.name + '-' + option?.email : 'Select user'}
                                    itemTemplate={formVehicleDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                        </div>
                        <div className="grid md:mt-2">
                            <div className="col-12  md:col-6">
                                <Label label={"Driver"} required={!updatedId}/>
                                <Dropdown
                                    options={userOptions ? userOptions : []}
                                    optionLabel="name"
                                    id="user_id"
                                    name={`user_id`}
                                    className={`w-full`}
                                    value={formData?.user_id}
                                    onChange={(e) => handleChangeData('user_id', e.value, formData, setFormData)}
                                    placeholder="Select driver"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setUserFilter(e.filter)
                                        }, 500);

                                        if (userRawData) {
                                            const modifiedData = userRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name,
                                                email: item?.email,
                                            }));
                                            setUserOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.name + '-' + option?.email : 'Select user'}
                                    itemTemplate={formDriverDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <Label label={"Status"} required={!updatedId}/>
                                <Dropdown
                                    options={statuses}
                                    id={"active_status"}
                                    name={"active_status"}
                                    optionLabel="name"
                                    placeholder="Select status"
                                    value={formData?.active_status}
                                    onChange={(e) => handleChangeData('active_status', e.value, formData, setFormData)}
                                    className="w-full"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={updatedId ? 'Update' : "Create"}
                                className="p-button-primary"
                                loading={isSubmitting}
                                size={'small'}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>

        </div>
    );
};

export default AssignDriver;